// =============================================================================
// Reset Password Page (password auth v1, 2026-09)
// =============================================================================
// Reads email+token from the emailed link's query string. On success all
// sessions are revoked server-side, so the user signs in fresh.
// =============================================================================

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { useResetPassword } from '@/hooks/useAuth';
import { PublicNavbar } from '@/components/PublicNavbar';
import { PageBackground } from '@/components/ui/PageBackground';
import { PasswordFields } from '@/components/auth/PasswordFields';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const email = useMemo(() => searchParams.get('email') ?? '', [searchParams]);
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const reset = useResetPassword();

  const linkInvalid = !email || !token;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (newPassword.length < 12) {
      setLocalError('Password must be at least 12 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    reset.mutate(
      { email, token, newPassword },
      {
        onSuccess: () => {
          navigate('/login', { replace: true });
        },
        onError: (err) => {
          const apiErr = err as { message?: string };
          setLocalError(apiErr.message || 'This reset link is invalid or has expired.');
        },
      }
    );
  };

  return (
    <PageBackground>
      <div className="min-h-screen">
        <PublicNavbar />
        <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
              Choose a new password
            </h2>
            {!linkInvalid && (
              <p className="mt-2 text-center text-sm text-gray-600">for {email}</p>
            )}
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {linkInvalid ? (
                <div className="text-center">
                  <p className="text-sm text-gray-700">
                    This reset link is incomplete. Please use the link from your email, or request
                    a new one.
                  </p>
                  <Link
                    to="/forgot-password"
                    className="mt-4 inline-block text-sm text-[#009FE4] hover:underline"
                  >
                    Request a new reset link
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <PasswordFields
                    newPassword={newPassword}
                    confirmPassword={confirmPassword}
                    onNewPasswordChange={setNewPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                    newPasswordLabel="New password"
                    error={localError}
                  />

                  {localError && <p className="text-sm text-red-600">{localError}</p>}

                  <button
                    type="submit"
                    disabled={reset.isPending}
                    className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#17DDAB] to-[#009FE4] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009FE4] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reset.isPending ? (
                      'Saving...'
                    ) : (
                      <>
                        <KeyRound className="w-4 h-4 mr-2" />
                        Set new password
                      </>
                    )}
                  </button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to sign in
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageBackground>
  );
}
